import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { inject, observer } from 'mobx-react';
import {
  Badge,
  Button,
  Collapse,
  Flex,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconClipboardText,
  IconClock,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DataCard from '../../../global/component/organism/DataCard/DataCard.tsx';
import { useGetTaskSummaries } from '../../../global/entities/Tasks/queries/useGetTaskSummaries.tsx';
import {
  isTaskAvailable,
  sortTasksByAvailability,
} from '../../../global/helpers/taskAvailability.ts';
import { getCarePlanPosition } from '../../../global/helpers/carePlanPosition.ts';
import { getCarePlanPhaseLabel } from '../../../global/helpers/carePlanPhase.ts';
import { TaskSummary } from '../../../global/entities/Tasks/Tasks.ts';
import { TaskType } from '../../../global/types';
import { routeStore } from '../../../global/Store/Store.ts';
import { useEICFConsentLock } from '../../../global/component/organism/EICFConsentGuard/eicfConsentLockContext.ts';
import { EICFConsentStatus } from '../../../global/entities/EICFConsents/EICFConsent.ts';
import { useGetMyClinicBranding } from '../../../global/entities/healthcareFacility/queries/useGetMyClinicBranding.tsx';
import ClinicTasksHeader from './ClinicTasksHeader.tsx';

// Types
export type ITasksBoardProps = object;

const EICF_CONSENT_PATH = '/patientDashboard/eicfConsent';

// The badge shows the UTC calendar day (toISOString), so the weekday must be
// derived in UTC too or behind-UTC browsers would name the previous day.
const formatDueDateWithWeekday = (
  dueDate: Date | null,
  language: string,
  dueDateLabel: string,
): string => {
  const date = new Date(dueDate ?? 0);
  const isoDay = date.toISOString().split('T')[0];
  const weekday = new Intl.DateTimeFormat(language, {
    weekday: 'long',
    timeZone: 'UTC',
  }).format(date);
  return `${dueDateLabel} ${isoDay} (${weekday})`;
};

const TasksBoard: React.FC<ITasksBoardProps> = inject()(
  observer(() => {
    // Hooks
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { data: taskSummaries } = useGetTaskSummaries();
    const { data: clinicBranding } = useGetMyClinicBranding();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [clickedTaskDueDate, setClickedTaskDueDate] = useState<string>('');
    const [isLockedModalOpen, setIsLockedModalOpen] = useState(false);
    const [showUpcoming, setShowUpcoming] = useState(false);
    const { isLocked, pendingStatus } = useEICFConsentLock();

    const isPatientSignaturePending =
      pendingStatus === EICFConsentStatus.PendingPatientSignature;

    // Task Navigation
    const onClickTask = useCallback(
      (taskSummary: TaskSummary) => {
        const id = !taskSummary.id
          ? taskSummary.collaboratorID
          : taskSummary.id;
        if (!taskSummary.id) navigate(`/patientDashboard/collaborator/${id}`);
        else if (taskSummary.taskTypeID === TaskType.counsellingAcknowledgement)
          navigate(`/patientDashboard/counsellingAcknowledgement/${id}`);
        else if (taskSummary.taskTypeID === TaskType.questionnaire)
          navigate(`/patientDashboard/questionnaire/${id}`);
        else navigate(`/patientDashboard/task/${id}`);
      },
      [navigate],
    );

    useEffect(() => {
      routeStore?.setFromLocation();
    }, []);

    const eicfCard: JSX.Element | null = useMemo(() => {
      if (!isLocked) return null;

      const title = isPatientSignaturePending
        ? t('eicf.task.signTitle')
        : t('eicf.task.awaitingTitle');
      const subtitle = isPatientSignaturePending
        ? t('eicf.task.signSubtitle')
        : t('eicf.task.awaitingSubtitle');

      return (
        <DataCard
          key="eicf-consent-task"
          title={
            <Flex align={'center'} gap={'xs'}>
              <Title order={4}>{title}</Title>
              {isPatientSignaturePending && (
                <Badge
                  color="red"
                  variant="filled"
                  size="sm"
                  leftSection={<IconAlertCircle size={12} />}
                >
                  {t('eicf.task.actionRequired')}
                </Badge>
              )}
            </Flex>
          }
          description={
            <Flex align={'center'}>
              <IconClipboardText
                color={'var(--mantine-color-dimmed)'}
                size={14}
              />
              <Text ml={'xs'} size={'sm'} c={'dimmed'}>
                {subtitle}
              </Text>
            </Flex>
          }
          leftBadgeText={t('eicf.task.badgeLabel')}
          leftBadgeProps={{
            color: isPatientSignaturePending ? 'red' : 'yellow',
          }}
          width={'100%'}
          onClick={
            isPatientSignaturePending
              ? () => navigate(EICF_CONSENT_PATH)
              : undefined
          }
          clickable={isPatientSignaturePending}
          readOnly={!isPatientSignaturePending}
          additionalAction={
            !isPatientSignaturePending ? (
              <IconClock size={20} color={'var(--mantine-color-yellow-7)'} />
            ) : undefined
          }
        />
      );
    }, [isLocked, isPatientSignaturePending, navigate, t, i18n.language]);

    const { availableCards, upcomingCards } = useMemo(() => {
      // Completable tasks first: a task the patient can do right now must
      // never sit below a locked future one, even when that one is due sooner.
      const sortedTasks = sortTasksByAvailability(taskSummaries ?? []);
      const toCard = (taskSummary: TaskSummary): JSX.Element => {
          // Check if task should be disabled (available date not reached).
          // Compare calendar dates (not timezone-sensitive Date parsing) so a task
          // serialised as UTC midnight is not unlocked a day early in UTC-negative zones.
          const isAvailable = isTaskAvailable(taskSummary.availableDate);
          const isUnavailable = !isAvailable;
          const isTaskDisabled = isUnavailable || isLocked;

          // Plan-position badge: only recurring care-plan tasks carry one.
          const planPosition = taskSummary.isRecurring
            ? getCarePlanPosition(
                taskSummary.recurrenceType,
                taskSummary.carePlanDayNumber,
              )
            : null;
          const planPositionText = planPosition
            ? planPosition.unit === 'day'
              ? t('tasksBoard.planDay', 'Day {{number}}', {
                  number: planPosition.value,
                })
              : planPosition.unit === 'week'
                ? t('tasksBoard.planWeek', 'Week {{number}}', {
                    number: planPosition.value,
                  })
                : t('tasksBoard.planMonth', 'Month {{number}}', {
                    number: planPosition.value,
                  })
            : undefined;
          const phaseLabel = getCarePlanPhaseLabel(
            taskSummary.carePlanSchedulingPhase,
            taskSummary.eventLabel,
          );
          const planBadgeText =
            planPositionText && phaseLabel
              ? `${planPositionText} (${t(phaseLabel.labelKey, phaseLabel.fallback, phaseLabel.values)})`
              : planPositionText;

          // Create a specific disabled click handler for this task
          const handleDisabledClick = () => {
            if (isLocked) {
              setIsLockedModalOpen(true);
              return;
            }
            const formattedDate = new Date(taskSummary.availableDate)
              .toISOString()
              .split('T')[0];
            setClickedTaskDueDate(formattedDate);
            setIsModalOpen(true);
          };

          return (
            <DataCard
              key={taskSummary.id}
              title={<Title order={4}>{taskSummary.description}</Title>}
              description={
                <Flex align={'center'}>
                  <IconClipboardText
                    color={'var(--mantine-color-dimmed)'}
                    size={14}
                  />
                  <Text ml={'xs'} size={'sm'} c={'dimmed'}>
                    {taskSummary.type}
                  </Text>
                </Flex>
              }
              leftBadgeText={formatDueDateWithWeekday(
                taskSummary.dueDate,
                i18n.language,
                t('tasksBoard.dueDateLabel', 'Due:'),
              )}
              recurringBadgeText={
                taskSummary.isRecurring
                  ? taskSummary.recurrenceType === 'DAILY_AM'
                    ? t('tasksBoard.recurringAm', 'Recurring (am)')
                    : taskSummary.recurrenceType === 'DAILY_PM'
                      ? t('tasksBoard.recurringPm', 'Recurring (pm)')
                      : taskSummary.recurrenceType === 'DAILY_NIGHT'
                        ? t('tasksBoard.recurringNight', 'Recurring (night)')
                        : t('tasksBoard.recurring', 'Recurring')
                  : undefined
              }
              rightBadgeText={taskSummary.isLate ? t('global.pastDue') : ''}
              leftBadgeProps={{
                color: taskSummary.isLate
                  ? 'red'
                  : isAvailable
                    ? 'green'
                    : 'gray',
              }}
              recurringBadgeProps={
                taskSummary.isRecurring
                  ? {
                      color: 'blue',
                    }
                  : undefined
              }
              planBadgeText={planBadgeText}
              planBadgeProps={{ color: 'grape' }}
              rightBadgeProps={{
                color: taskSummary.isLate ? 'red' : 'gray',
              }}
              width={'100%'}
              onClick={() => onClickTask(taskSummary)}
              onDisabledClick={isTaskDisabled ? handleDisabledClick : undefined}
              clickable
              disabled={isTaskDisabled}
            />
          );
      };
      return {
        availableCards: sortedTasks
          .filter((taskSummary) => isTaskAvailable(taskSummary.availableDate))
          .map(toCard),
        upcomingCards: sortedTasks
          .filter((taskSummary) => !isTaskAvailable(taskSummary.availableDate))
          .map(toCard),
      };
    }, [onClickTask, taskSummaries, isLocked, t, i18n.language]);

    const availableTaskCount = useMemo(
      () =>
        taskSummaries?.filter((taskSummary: TaskSummary) =>
          isTaskAvailable(taskSummary.availableDate),
        ).length ?? 0,
      [taskSummaries],
    );

    const totalCount = availableTaskCount + (eicfCard ? 1 : 0);

    return (
      <>
        <Paper w="100%" mt={'lg'}>
          <ClinicTasksHeader
            logoUrl={clinicBranding?.logoUrl}
            clinicName={clinicBranding?.name}
            count={totalCount}
          />
          <Stack align={'center'} justify={'space-between'} mb={'md'}>
            {eicfCard}
            {availableCards}
            {upcomingCards.length > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowUpcoming((shown) => !shown)}
                >
                  {showUpcoming
                    ? t('tasksBoard.hideUpcomingTasks', 'Hide upcoming tasks')
                    : t(
                        'tasksBoard.upcomingTasks',
                        'Upcoming tasks ({{count}})',
                        { count: upcomingCards.length },
                      )}
                </Button>
                <Collapse in={showUpcoming} w="100%">
                  <Stack align={'center'}>{upcomingCards}</Stack>
                </Collapse>
              </>
            )}
          </Stack>
        </Paper>

        <Modal
          opened={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={t('tasksBoard.taskNotAvailable', 'Task Not Available')}
          centered
          size="md"
        >
          <Stack gap="md">
            <Text>
              {t(
                'tasksBoard.taskNotAvailableMessage',
                'This task cannot be completed right now. Please come back by {{dueDate}}.',
                { dueDate: clickedTaskDueDate },
              )}
            </Text>
            <Button
              onClick={() => setIsModalOpen(false)}
              color="blue"
              fullWidth
            >
              {t('global.close', 'Close')}
            </Button>
          </Stack>
        </Modal>

        <Modal
          opened={isLockedModalOpen}
          onClose={() => setIsLockedModalOpen(false)}
          title={t('eicf.lock.modalTitle')}
          centered
          size="md"
        >
          <Stack gap="md">
            <Text>
              {isPatientSignaturePending
                ? t('eicf.lock.taskBlocked')
                : t('eicf.lock.taskBlockedAwaiting')}
            </Text>
            {isPatientSignaturePending && (
              <Button
                onClick={() => {
                  setIsLockedModalOpen(false);
                  navigate(EICF_CONSENT_PATH);
                }}
                color="red"
                fullWidth
              >
                {t('eicf.task.signTitle')}
              </Button>
            )}
            <Button
              onClick={() => setIsLockedModalOpen(false)}
              variant="default"
              fullWidth
            >
              {t('global.close', 'Close')}
            </Button>
          </Stack>
        </Modal>
      </>
    );
  }),
);

export default TasksBoard;
